import { Injectable } from "@angular/core";
import { BehaviorSubject, of } from "rxjs"
import * as utils from '../../utils/utility-functions';
import { EmailNotificationService } from "../notification.service";
import { AngularFirestore, AngularFirestoreCollection } from "@angular/fire/compat/firestore";
import { UserTypeEnum } from "src/app/models/user-type";
import * as moment from "moment-timezone";
import { Doctor, DoctorType } from "src/app/models/doctor.model";
import { AngularFireStorage } from "@angular/fire/compat/storage";
import { switchMap } from "rxjs/operators";
import { AngularFireAuth } from "@angular/fire/compat/auth";
import { AngularFireFunctions } from "@angular/fire/compat/functions";


@Injectable({
  providedIn: 'root',
})

export class DoctorService {
  private firebaseInvitedCollection: AngularFirestoreCollection;
  private firebaseDoctorCollection: AngularFirestoreCollection;
  private firebaseOrgLocationUserCollection: AngularFirestoreCollection;

  private doctor = new BehaviorSubject(null);
  currentDoctor = this.doctor.asObservable();

  private doctors = new BehaviorSubject([]);
  currentDoctors = this.doctors.asObservable();

  private invitedDoctors = new BehaviorSubject([]);
  currentInvitedDoctors = this.invitedDoctors.asObservable();

  private doctorProfileImage = new BehaviorSubject(null);
  currentDoctorProfileImage = this.doctorProfileImage.asObservable();


  constructor(
    private emailNotificationService: EmailNotificationService,
    private angularFirestore: AngularFirestore,
    private storage: AngularFireStorage,
    private afAuth: AngularFireAuth,
    private angularFunctions: AngularFireFunctions
  ) {
    this.firebaseInvitedCollection = this.angularFirestore.collection('Invited');
  }

  async getAllDoctors() {
    try {
      let doctors = [];
      this.doctors.next([]);

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      await this.firebaseDoctorCollection.ref
        .orderBy('last_name')
        .orderBy('first_name')
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            doctors.push(docData)
          });
        }).catch(err => console.log("getAllDoctors query 1 error: " + err));

      this.updateDoctors(doctors);
      return doctors;
    } catch (error) {
      console.log(error);
    }
  }

  async getAllInvitedDoctors() {
    try {
      let doctors = [];

      if (this.firebaseInvitedCollection == null) {
        this.firebaseInvitedCollection = this.angularFirestore.collection('Invited');
      }

      await this.firebaseInvitedCollection.ref
        .where('type', '==', 0)
        .where('accepted', '==', false)
        .orderBy('last_name')
        .orderBy('first_name')
        .get()
        .then(querySnapshot => {
          querySnapshot.forEach(doc => {
            const docData = {
              id: doc.id,
              ...doc.data()
            }
            doctors.push(docData)
          });
        }).catch(err => console.log("getAllInvitedDoctors query 1 error: " + err));

      this.updateInvtedDoctors(doctors);
      return doctors;
    } catch (error) {
      console.log(error);
    }
  }

  async getDoctorById(id: string) {
    try {
      let doctor = null;

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      await this.firebaseDoctorCollection.ref
        .doc(id)
        .get()
        .then(querySnapshot => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          doctor = docData;
        }).catch(err => console.log("getDoctorById query 1 error: " + err));

      this.updateDoctor(doctor);
      return doctor;
    } catch (error) {
      console.log(error);
    }
  }

  getDoctorByIdAsync(id: string) {
    return this.afAuth.authState.pipe(
      switchMap(user => {
        if (user) {
          return this.angularFirestore.collection<Doctor>('Doctors')
            .doc(id)
            .valueChanges({ idField: 'id' });
        } else {
          return of(null);
        }
      })
    );
  }

  async getDoctorsById(ids: string[]) {
    try {
      let orgLocationUsers = [];
      let idPromises = [];

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      ids.map((currendID: any) => {
        const promiseCreated = new Promise((resolve, reject) => {
          resolve(this.firebaseDoctorCollection.ref
            .doc(currendID)
            .get());
        }).then((querySnapshot: any) => {
          const docData = {
            id: querySnapshot.id,
            ...querySnapshot.data()
          }
          orgLocationUsers.push(docData)
        });
        idPromises.push(promiseCreated);
      })

      await Promise.all(idPromises);

      return orgLocationUsers;
    } catch (error) {
      console.log(error);
    }
  }

  async saveLocationToDoctor(location: any, doctorID: string) {
    try {
      const newLocation = utils.createOrgLocationUserObject(location, doctorID);

      if (this.firebaseOrgLocationUserCollection == null) {
        this.firebaseOrgLocationUserCollection = this.angularFirestore.collection('OrgLocationUser');
      }

      const createdUserLocation = await this.firebaseOrgLocationUserCollection
        .add(JSON.parse(JSON.stringify(newLocation)));

      const convertCreatedUserLocation = {
        id: createdUserLocation.id,
        ...newLocation
      }

      return convertCreatedUserLocation;
    } catch (error) {
      console.log(error);
    }
  }

  async createDoctor(doctor: any) {
    try {
      let newDoctor = utils.createDoctorObject(doctor);
      newDoctor.status = false;

      const doctorRef = (await this.firebaseInvitedCollection.doc(doctor.email).ref.get()).exists;
      if (doctorRef) { return false };

      const doctorType = doctor.locationType == DoctorType.HOSPITAL ? UserTypeEnum.DOCTOR : UserTypeEnum.FIRST_AID;

      await this.firebaseInvitedCollection.doc(doctor.email.toLowerCase().trim()).set({
        ...newDoctor,
        type: doctorType,
        accepted: false,
        org_id: doctor.location.org_id,
        org_location_id: doctor.location.id || doctor.location.id,
        location_name: doctor.location.location_name,
        created_at: Number(moment().format('x'))
      });

      this.emailNotificationService.sendDoctorInvitationEmail(newDoctor.email, `${newDoctor.first_name} ${newDoctor.last_name}`);

      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async updateDoctorAsync(Doctor: any) {
    try {
      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      let { id, ...DoctorToSave } = Doctor;
      DoctorToSave.updated_at = Number(moment().format('x'));
      DoctorToSave.first_name = DoctorToSave.first_name.trim().toLowerCase();
      DoctorToSave.last_name = DoctorToSave.last_name.trim().toLowerCase();

      await this.firebaseDoctorCollection.ref
        .doc(id)
        .update(JSON.parse(JSON.stringify(DoctorToSave)))
        .catch(err => console.log("updateDoctor query 1 error: " + err));

      DoctorToSave.id = id;
      this.updateDoctor(DoctorToSave);

      return true;
    } catch (error) {
      console.log('createDoctor', error);
      return false;
    }
  }

  async updateDoctorPhone(doctorID: string, phone: string) {
    try {
      const callable = this.angularFunctions.httpsCallable('updateUserPhoneNumber');

      await callable({
        user_id: doctorID,
        phone: phone
      })
        .toPromise()

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      await this.firebaseDoctorCollection.ref
        .doc(doctorID)
        .update({
          phone: phone,
          updated_at: moment().format('x')
        }).catch(err => console.log("updateExpertPhoneNumber doctor update 1 error: " + err));

        let updatedDoctor = this.doctor.getValue();
        updatedDoctor.phone = phone;

        this.updateDoctor(updatedDoctor);

      return true;

    } catch (error) {
      console.log(error);
      return false;
    }
  }

  async disableDoctorAccount(doctor: any) {
    try {
      const callable = this.angularFunctions.httpsCallable('disableAccount');

      callable({
        user_id: doctor.id,
      })
        .toPromise()
        .then((result) => {
          console.log('disableDoctorAccount', result);
        })
        .catch((error) => console.log('disableDoctorAccount', error));

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      await this.firebaseDoctorCollection.ref
        .doc(doctor.id)
        .update({
          status: false
        })
        .catch(err => console.log("disableDoctorAccount query 1 error: " + err));

      return true;


    } catch (error) {
      console.log('disableDoctorAccount', error)
    }
  }

  async enableDoctorAccount(doctor: any) {
    try {
      const callable = this.angularFunctions.httpsCallable('enableAccount');

      callable({
        user_id: doctor.id,
      })
        .toPromise()
        .then((result) => {
          console.log('enableDoctorAccount', result);
        })
        .catch((error) => console.log('enableDoctorAccount', error));

      if (this.firebaseDoctorCollection == null) {
        this.firebaseDoctorCollection = this.angularFirestore.collection('Doctors');
      }

      await this.firebaseDoctorCollection.ref
        .doc(doctor.id)
        .update({
          status: true
        })
        .catch(err => console.log("disableDoctorAccount query 1 error: " + err));

      return true;
    } catch (error) {
      console.log('enableDoctorAccount', error)
    }
  }

  // Async function to save image file to firebase storage
  async saveDoctorProfileImage(image: any) {
    try {
      let downloadURL;

      const fileName = moment().format('x').toString() + '_' + image.name;
      const remotePath = 'uploads/images/' + fileName;
      const fileRef = this.storage.ref(remotePath)

      await this.storage.upload(remotePath, image)
        .catch((error) => {
          console.log('saveImage upload error', error)
          return null;
        });

      await fileRef.getDownloadURL().toPromise()
        .then((result) => {
          downloadURL = result;
        })
        .catch((error) => {
          console.log('saveImage getDownloadURL error', error)
          return null;
        });

      return downloadURL;
    } catch (error) {
      console.log(error);
    }
  }

  updateProfileImage(imageURL: string) {
    this.doctorProfileImage.next(imageURL);
  }

  updateDoctor(doctorResult: any) {
    let newDoctor = utils.createDoctor(doctorResult);
    this.doctor.next(newDoctor);
  }

  updateInvtedDoctors(doctorResult: any) {
    let newDoctorList = []
    doctorResult.forEach(doctor => {
      let newDoctor = doctor;
      newDoctor.created_at = moment(newDoctor.created_at, 'x').format('DD MMM YYYY').toString() || '';
      newDoctorList.push(doctor);
    });

    this.invitedDoctors.next(newDoctorList);
  }

  updateDoctors(doctorResult: any) {
    let newDoctorList = []
    doctorResult.forEach(doctor => {
      let newDoctor = utils.createDoctor(doctor);
      newDoctorList.push(newDoctor);
    });

    this.doctors.next(newDoctorList);
  }
}